import { useCallback, useEffect, useState } from 'react';
import { DropzoneOptions, DropzoneState, useDropzone } from 'react-dropzone';
import { TempFile, useUploadTempFile } from './useUploadTempFile';
import useNotify from './useNotify';

type UseFileOptions = Pick<
  DropzoneOptions,
  | 'maxSize'
  | 'minSize'
  | 'accept'
  | 'disabled'
  | 'multiple'
  | 'noDrag'
  | 'validator'
  | 'onDrop'
>;

function useFileUpload(options?: UseFileOptions) {
  const { showError } = useNotify();
  const { uploadTempFile, uploadTempFiles, loading } = useUploadTempFile();

  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const maxSize = options?.maxSize ?? 10000000;

  const dropZone: DropzoneState = useDropzone({
    ...options,
    onDropRejected: (rejection) => {
      showError(
        `Error selecting file${options?.multiple === false ? '' : 's'}`,
        rejection[0].errors[0].message
      );
    },
    onError: (error) => {
      showError(
        `Error selecting file${options?.multiple === false ? '' : 's'}`,
        error
      );
    },
    maxSize,
  });
  const { acceptedFiles } = dropZone;

  // create a preview as a side effect, whenever selected file is changed
  useEffect(() => {
    if (!acceptedFiles?.length) {
      setPreviewUrls([]);
      return;
    }

    const objectUrls = acceptedFiles.map((selectedFile) =>
      URL.createObjectURL(selectedFile)
    );
    setPreviewUrls(objectUrls);

    // free memory when ever this component is unmounted
    return () =>
      objectUrls.forEach((objectUrl) => URL.revokeObjectURL(objectUrl));
  }, [acceptedFiles, setPreviewUrls]);

  const doUpload = useCallback(async (): Promise<TempFile[]> => {
    if (!acceptedFiles?.length) {
      throw new Error(
        `No file${options?.multiple === false ? '' : 's'} selected`
      );
    }
    return uploadTempFiles(acceptedFiles);
  }, [uploadTempFiles, acceptedFiles]);

  return {
    ...dropZone,
    acceptedFiles,
    acceptedFile: acceptedFiles[0],
    previewUrls,
    previewUrl: previewUrls[0],
    doUpload,
    uploadTempFile,
    uploadTempFiles,
    loading,
  };
}

export default useFileUpload;
